import { useState } from 'react';
import { fmtDateTime, ROLES, type RoleAssignmentDTO, type UserDTO } from '@em/shared';
import { api, qk, queryClient, useEmployees, useSettings, useTeams, useUsers } from '../../api';
import { useSession } from '../../auth/AuthContext';
import { ROLE_LABELS } from '../../components/format';
import { Modal } from '../../components/Modal';
import { PromptDialog } from '../../components/PromptDialog';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, Checkbox, EmptyState, ErrorAlert, Field, Input, QueryState, Select } from '../../components/ui';

const reload = () => {
  void queryClient.invalidateQueries({ queryKey: qk.users });
  void queryClient.invalidateQueries({ queryKey: qk.teams });
};

export function UsersSettings() {
  const q = useUsers();
  const session = useSession();
  const settings = useSettings();
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const toast = useToast();
  const [editing, setEditing] = useState<UserDTO | 'new' | null>(null);
  const [secret, setSecret] = useState<{ name: string; email: string; password: string } | null>(null);
  const [confirm, setConfirm] = useState<{ user: UserDTO; action: 'password' | 'mfa' | 'disable' | 'enable' } | null>(null);

  return (
    <Card
      title="Users and roles"
      tight
      actions={
        <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
          Add user
        </Button>
      }
    >
      <QueryState q={q}>
        {(list) =>
          list.length === 0 ? (
            <EmptyState title="No users" />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Roles</th>
                    <th>Authenticator</th>
                    <th>Last sign-in</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <strong>{u.name}</strong>
                        {u.id === session.user.id && <Badge tone="accent">You</Badge>}
                        <div className="small muted">{u.email}</div>
                      </td>
                      <td>
                        {u.roles.map((r) => (
                          <Badge key={r.role}>{ROLE_LABELS[r.role]}</Badge>
                        ))}
                      </td>
                      <td>{u.mfaEnabled ? <Badge tone="green">Set up</Badge> : <Badge tone="amber">Not set up</Badge>}</td>
                      <td className="small nowrap">{u.lastLoginAt ? fmtDateTime(u.lastLoginAt, tz) : <span className="muted">Never</span>}</td>
                      <td>{u.status === 'active' ? <Badge tone="green">Active</Badge> : <Badge tone="red">Disabled</Badge>}</td>
                      <td className="actions">
                        <Button size="sm" onClick={() => setEditing(u)}>
                          Manage
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </QueryState>

      {editing && (
        <UserDialog
          user={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onCreated={(name, email, password) => setSecret({ name, email, password })}
          onAction={(user, action) => {
            setEditing(null);
            setConfirm({ user, action });
          }}
        />
      )}
      {confirm && (
        <PromptDialog
          hideNote
          title={
            confirm.action === 'password' ? `Reset password for ${confirm.user.name}` : confirm.action === 'mfa' ? `Reset authenticator for ${confirm.user.name}` : `${confirm.action === 'disable' ? 'Disable' : 'Enable'} ${confirm.user.name}`
          }
          description={
            confirm.action === 'password'
              ? 'A temporary password is created and their sessions end. They must choose a new password when they sign in.'
              : confirm.action === 'mfa'
                ? 'Their authenticator is removed and their sessions end. They set up a new one at their next sign-in.'
                : confirm.action === 'disable'
                  ? 'They are signed out everywhere and cannot sign in. Their history is kept.'
                  : 'They can sign in again.'
          }
          danger={confirm.action !== 'enable'}
          confirmLabel={confirm.action === 'password' ? 'Reset password' : confirm.action === 'mfa' ? 'Reset authenticator' : confirm.action === 'disable' ? 'Disable user' : 'Enable user'}
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            const u = confirm.user;
            if (confirm.action === 'password') {
              const r = await api.resetUserPassword(u.id);
              setSecret({ name: u.name, email: u.email, password: r.temporaryPassword });
            } else if (confirm.action === 'mfa') {
              await api.resetUserMfa(u.id);
              toast.success('Authenticator reset');
            } else {
              await api.updateUser(u.id, { status: confirm.action === 'disable' ? 'disabled' : 'active' });
              toast.success(confirm.action === 'disable' ? 'User disabled' : 'User enabled');
            }
            reload();
          }}
        />
      )}
      {secret && (
        <Modal title="Temporary password" onClose={() => setSecret(null)} footer={<Button variant="primary" onClick={() => setSecret(null)}>I have passed it on</Button>}>
          <div className="stack">
            <p>
              Give this to <strong>{secret.name}</strong> ({secret.email}) in person or by phone. It is shown only once. They choose their own password and set up an authenticator when they first sign in.
            </p>
            <div className="big-code" style={{ fontSize: 24, letterSpacing: '0.05em' }}>
              {secret.password}
            </div>
            <Alert tone="amber">Do not email it together with the sign-in address.</Alert>
          </div>
        </Modal>
      )}
    </Card>
  );
}

function UserDialog({
  user,
  onClose,
  onCreated,
  onAction,
}: {
  user?: UserDTO;
  onClose: () => void;
  onCreated: (name: string, email: string, password: string) => void;
  onAction: (user: UserDTO, action: 'password' | 'mfa' | 'disable' | 'enable') => void;
}) {
  const session = useSession();
  const teams = useTeams();
  const employees = useEmployees({ status: 'active' });
  const toast = useToast();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [employeeId, setEmployeeId] = useState(user?.employeeId ?? '');
  const [roles, setRoles] = useState<RoleAssignmentDTO[]>(user?.roles ?? [{ role: 'supervisor', teamIds: [] }]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const has = (r: RoleAssignmentDTO['role']) => roles.some((x) => x.role === r);
  const sup = roles.find((r) => r.role === 'supervisor');
  const toggle = (r: RoleAssignmentDTO['role'], on: boolean) => setRoles((rs) => (on ? [...rs, { role: r, teamIds: r === 'supervisor' ? [] : null }] : rs.filter((x) => x.role !== r)));
  const setSupTeams = (ids: string[]) => setRoles((rs) => rs.map((r) => (r.role === 'supervisor' ? { ...r, teamIds: ids } : r)));
  const selfOwnerLoss = user?.id === session.user.id && !has('owner');

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (user) {
        await api.updateUser(user.id, { name: name.trim(), roles, employeeId: employeeId || null });
        toast.success('User saved');
      } else {
        const r = await api.createUser({ email: email.trim(), name: name.trim(), roles, employeeId: employeeId || null });
        onCreated(r.user.name, r.user.email, r.temporaryPassword);
      }
      reload();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={user ? `Edit ${user.name}` : 'Add user'}
      onClose={onClose}
      onSubmit={save}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!name.trim() || (!user && !email.trim()) || roles.length === 0}>
            {user ? 'Save' : 'Add user'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorAlert error={error} />
        <Field label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="Email" required={!user} hint={user ? 'The sign-in email cannot be changed' : undefined}>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!!user} required={!user} />
        </Field>
        <Field label="Linked employee" hint="If this person also clocks in. Stops them approving their own time.">
          <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">Not an employee</option>
            {employees.data?.map((e) => (
              <option key={e.id} value={e.id}>
                {e.displayName} ({e.employeeNumber})
              </option>
            ))}
          </Select>
        </Field>
        <div className="field">
          <span className="label">Roles</span>
          {ROLES.map((r) => (
            <Checkbox key={r} label={ROLE_LABELS[r]} checked={has(r)} onChange={(on) => toggle(r, on)} />
          ))}
        </div>
        {sup && (
          <div className="field">
            <span className="label">Supervises teams</span>
            {teams.data?.map((t) => (
              <Checkbox key={t.id} label={t.name} checked={sup.teamIds?.includes(t.id) ?? false} onChange={(on) => setSupTeams(on ? [...(sup.teamIds ?? []), t.id] : (sup.teamIds ?? []).filter((x) => x !== t.id))} />
            ))}
            <span className="hint">Payroll admins and owners see every team.</span>
          </div>
        )}
        {selfOwnerLoss && <Alert tone="red">You cannot remove your own owner role.</Alert>}
        {user && (
          <fieldset>
            <legend>Sign-in and security</legend>
            <div className="row">
              <Button size="sm" onClick={() => onAction(user, 'password')}>
                Reset password
              </Button>
              <Button size="sm" onClick={() => onAction(user, 'mfa')} disabled={!user.mfaEnabled}>
                Reset authenticator
              </Button>
              {user.id !== session.user.id &&
                (user.status === 'active' ? (
                  <Button size="sm" variant="danger" onClick={() => onAction(user, 'disable')}>
                    Disable user
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => onAction(user, 'enable')}>
                    Enable user
                  </Button>
                ))}
            </div>
          </fieldset>
        )}
      </div>
    </Modal>
  );
}
